import { SvgIconComponent } from '@mui/icons-material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceTwoTone';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FmdGoodIcon from '@mui/icons-material/FmdGoodTwoTone';
import TodayIcon from '@mui/icons-material/TodayTwoTone';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremiumTwoTone';
import WorkIcon from '@mui/icons-material/WorkTwoTone';
import { Button, Card, CardContent, Grid2, Typography } from '@mui/material';
import { differenceInYears } from 'date-fns';
import React, { ReactElement } from 'react';
import {
	HStack,
	Motion,
	Section,
	SectionHeader,
	TypographyWithBorder,
} from 'src/components';
import { DEFAULT_MOTION_SCALE, Sections } from 'src/constant';

export interface AboutMeSectionProps {}

export const AboutMeSection: React.FC<AboutMeSectionProps> = () => {
	return (
		<Section id={Sections.AboutMe}>
			<SectionHeader
				description='Hello! I’m Kerolos Magdy, a frontend developer passionate about
          building clean, responsive, and interactive user interfaces using
          React, JavaScript, and TypeScript. I enjoy solving complex problems
          and continually learning new technologies.'
				title='About Me'
			/>
			<Grid2
				container
				spacing={2}
				sx={{ justifyContent: 'center' }}
			>
				{(Object.entries(aboutMeConfig) as [string, AboutMeItem[]][]).map(
					([key, items]) => (
						<Grid2
							key={key}
							size={{ md: 6, xs: 12 }}
						>
							<Motion style={{ height: '100%' }}>
								<Card
									sx={{
										height: '100%',
										overflow: 'hidden',
										position: 'relative',
									}}
								>
									{items[0]?.icon
										? (() => {
												const Icon = items[0].icon;
												return (
													<Icon
														sx={theme => ({
															bottom: theme.spacing(2),
															color: theme.palette.text.primary,
															fontSize: { md: 180, xs: 120 },
															opacity: 0.05,
															pointerEvents: 'none',
															position: 'absolute',
															right: theme.spacing(2),
														})}
													/>
												);
											})()
										: null}

									<CardContent
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: 1,
										}}
									>
										<TypographyWithBorder variant='h4'>
											{key}
										</TypographyWithBorder>
										{items.map((item: AboutMeItem) => {
											const { cta, icon: Icon, text } = item;
											return (
												<HStack key={JSON.stringify(item)}>
													{Icon ? (
														<Icon sx={{ color: 'text.secondary' }} />
													) : null}
													{text ? (
														<Typography variant='body1'>{text}</Typography>
													) : null}
													{cta ? (
														<Motion whileHover={DEFAULT_MOTION_SCALE}>
															{cta}
														</Motion>
													) : null}
												</HStack>
											);
										})}
									</CardContent>
								</Card>
							</Motion>
						</Grid2>
					),
				)}
			</Grid2>
		</Section>
	);
};

export interface AboutMeItem {
	cta?: ReactElement;
	icon?: SvgIconComponent;
	onClick?: () => void;
	text?: string;
}

export interface AboutMeConfig {
	education: AboutMeItem[];
	personal: AboutMeItem[];
}

const aboutMeConfig: AboutMeConfig = {
	education: [
		{
			icon: AccountBalanceIcon,
			text: 'Faculty of Business MIS Department',
		},
		{ icon: FmdGoodIcon, text: 'Alexandria, Egypt' },
		{ icon: WorkspacePremiumIcon, text: 'Very Good with Honer' },
		{ icon: TodayIcon, text: '2018 - 2022' },
	],
	personal: [
		{ icon: FmdGoodIcon, text: 'Alexandria, Egypt' },
		{
			icon: TodayIcon,
			text: differenceInYears(new Date(), new Date(2000, 10, 14)) + ' Years',
		},
		{ icon: WorkIcon, text: 'Available to work' },
		{
			cta: (
				<Button
					download
					href='assets/Kerolos-Magdy-Resume.pdf'
					startIcon={<FileDownloadOutlinedIcon />}
					variant='contained'
				>
					Hire Me
				</Button>
			),
		},
	],
};
